import React from 'react';
import { TempFooterProps as IBlockProps } from './Footer.interface';
import { Footer } from './Footer.component';
import TranslationComponent from '@/utils/TranslationComponent/TranslationComponent';
import * as translation from '@/utils/TranslationComponent/translation.utilities';

interface ICmsImage {
  alternateText: string;
  defaultSrc: string;
  sizesAttribute: string[];
  srcSet: string[];
  srcSetWebp: string[];
  isBackground: boolean;
  textContent: string;
  isLazy: boolean;
}

interface ICmsInclusionIcon {
  image?: ICmsImage;
  announceToScreenReader?: boolean;
}

interface IBlockInputProps {
  name: string;
  hideFooterBanner?: boolean;
  props: {
    isCampaign: boolean;
    footerLogo: ICmsImage;
    supportEnquiryText: string;
    supportEnquiryLabel: string;
    supportCta: {
      text: string;
      target?: HTMLAnchorElement['target'];
      href: HTMLAnchorElement['href'];
      title?: string;
      ariaLabel?: string;
    };
    copyrightHeading: string;
    acknowledgementToCountry: string;
    acknowledgementToCountryImage: ICmsImage;
    inclusionLogos?: ICmsImage[];
    inclusionIcons?: ICmsInclusionIcon[];
    inclusionBackgroundColor?: ThemeColors | string;
    socials: Array<{
      icon: string;
      link: {
        text?: string;
        target?: HTMLAnchorElement['target'];
        href: HTMLAnchorElement['href'];
        title?: string;
        ariaLabel?: string;
      };
    }>;
    footerNavigation: Array<{
      header: Array<{
        text: string;
        target?: HTMLAnchorElement['target'];
        href: HTMLAnchorElement['href'];
        title?: string;
        ariaLabel?: string;
      }>;
      links: Array<{
        text: string;
        target?: HTMLAnchorElement['target'];
        href: HTMLAnchorElement['href'];
        title?: string;
        ariaLabel?: string;
      }>;
    }>;
    utilityLink: Array<{
      text: string;
      target?: HTMLAnchorElement['target'];
      href: HTMLAnchorElement['href'];
      title?: string;
      ariaLabel?: string;
    }>;
    campaignLink:
      | {
          text: string;
          target?: HTMLAnchorElement['target'];
          href: HTMLAnchorElement['href'];
          title?: string;
          ariaLabel?: string;
        }
      | undefined;
  };
  [key: string]: any; // Allow passthrough of other props like block settings
}

const mapInputToProps = (input: IBlockInputProps): IBlockProps => {
  const {
    acknowledgementToCountryImage,
    acknowledgementToCountry,
    inclusionLogos,
    inclusionIcons,
    inclusionBackgroundColor,
    footerLogo,
    supportEnquiryText,
    supportEnquiryLabel,
    supportCta,
    hideFooterBanner,
    copyrightHeading,
    socials,
    footerNavigation,
    utilityLink,
  } = input;

  const mappedInclusionIcons: ICmsInclusionIcon[] | undefined =
    inclusionIcons && inclusionIcons.length > 0
      ? inclusionIcons
      : acknowledgementToCountryImage
        ? [{ image: acknowledgementToCountryImage }]
        : undefined;
  const mappedInclusionLogos: ICmsImage[] | undefined = inclusionLogos;

  return {
    welcomeText: acknowledgementToCountry || undefined,
    inclusionLogos: mappedInclusionLogos
      ?.filter((logoItem) => Boolean(logoItem.defaultSrc))
      .slice(0, 2)
      .map((logoItem) => translation.mapImageProps(logoItem)),
    inclusionIcons: mappedInclusionIcons
      ?.slice(0, 6)
      .map((icon) => ({
        image: translation.mapImageProps(icon.image ?? icon),
        announceToScreenReader: icon.announceToScreenReader,
      })),
    inclusionBackgroundColor: inclusionBackgroundColor || undefined,
    logo: footerLogo || undefined,
    socialLinks:
      socials.length > 0
        ? socials.map((social: any) => {
            return {
              icon: social?.icon || 'chevron-right',
              url: social?.link?.href || '#',
              label: social?.link?.text || 'DEFAULT TEXT',
            };
          })
        : undefined,
    linkLists:
      footerNavigation.length > 0
        ? footerNavigation.map((list: any) => {
            return {
              title: list.header || undefined,
              links: list.links || undefined,
            };
          })
        : undefined,
    copyrightText: copyrightHeading || undefined,
    legalLinks:
      utilityLink.length > 0
        ? utilityLink?.map((link: any) => {
            return {
              text: link.text || 'DEFAULT TEXT',
              href: link.href || '#',
              target: link.target || '_self',
              title: link.title || '',
              ariaLabel: link.ariaLabel || '',
            };
          })
        : undefined,
    supportCta:
      !hideFooterBanner &&
      (supportCta || supportEnquiryText || supportEnquiryLabel)
        ? {
            title: `${supportEnquiryLabel} <span class='supportCta__title--phone'>${supportEnquiryText || 'INSERT NUMBER'}</span>`,
            button: supportCta
              ? translation.mapAnchorProps({ ...supportCta })
              : undefined,
          }
        : undefined,
  };
};

// The component that uses the generic TranslationComponent
const BlockTranslation: React.FC<IBlockInputProps> = (props) => {
  return (
    <TranslationComponent
      inputProps={props}
      TargetComponent={Footer}
      mapProps={mapInputToProps}
    />
  );
};

export default BlockTranslation;
